import { Badge, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, cn } from "@nexus/ui";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { scriptSlotLabel } from "../../../lib/persona-script-meta.js";

import type { HubChromeScriptRow } from "./hub-chrome.types.js";

type Translate = ReturnType<typeof useTranslation>["t"];

const TOOLTIP_DELAY_MS = 300;

/**
 * Names whether a hub row is a script or a brief, in the same tones as the
 * workspace badges: sent blue for a script, AI mint for a brief.
 * Hovering lists the document's parameters.
 */
export function OpportunityHubDocumentKindBadge({
  row,
  className,
}: {
  row: HubChromeScriptRow;
  className?: string;
}) {
  const { t } = useTranslation();
  const isBrief = row.kind === "brief";

  return (
    <TooltipProvider delayDuration={TOOLTIP_DELAY_MS}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            variant={isBrief ? "ai" : "status-sent"}
            iconStart={isBrief ? "file-text" : "clapperboard"}
            className={cn("text-[11px]", className)}
            data-testid="opportunity-hub-kind-badge"
          >
            {kindLabel(t, row.kind)}
          </Badge>
        </TooltipTrigger>
        <TooltipContent align="start" className="px-3 py-2">
          <DocumentParams row={row} t={t} />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function DocumentParams({ row, t }: { row: HubChromeScriptRow; t: Translate }) {
  const isBrief = row.kind === "brief";
  const partCount = isBrief ? row.hookPackage : row.sceneCount;
  const duration = row.duration?.trim();

  return (
    <div className="flex max-w-64 flex-col gap-1.5" data-testid="opportunity-hub-kind-params">
      <div className="flex flex-col gap-0.5">
        <p className="font-medium">{kindLabel(t, row.kind)}</p>
        <p className="opacity-70">
          {isBrief
            ? t(
                "personaOpportunities.hub.scripts.kindHintBrief",
                "Creator brief — concept, CTA and hooks to choose from.",
              )
            : t(
                "personaOpportunities.hub.scripts.kindHintScript",
                "Scene-by-scene shooting script — visuals, voiceover, product and props.",
              )}
        </p>
      </div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
        <DocumentParam label={t("personaOpportunities.hub.scripts.kindParams.slot", "Slot")}>
          {scriptSlotLabel(row.slotIndex, t, row.variantMeta)}
        </DocumentParam>
        {partCount != null ? (
          <DocumentParam
            label={
              isBrief
                ? t("personaOpportunities.hub.scripts.kindParams.hooks", "Hooks")
                : t("personaOpportunities.hub.scripts.kindParams.scenes", "Scenes")
            }
          >
            {partCount}
          </DocumentParam>
        ) : null}
        {duration ? (
          <DocumentParam
            label={t("personaOpportunities.hub.scripts.kindParams.duration", "Duration")}
          >
            {duration}
          </DocumentParam>
        ) : null}
        <DocumentParam
          label={t("personaOpportunities.hub.scripts.kindParams.versions", "Versions")}
        >
          {row.versionCount}
        </DocumentParam>
      </dl>
    </div>
  );
}

function DocumentParam({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="opacity-70">{label}</dt>
      <dd className="tabular-nums">{children}</dd>
    </>
  );
}

function kindLabel(t: Translate, kind: HubChromeScriptRow["kind"]): string {
  return kind === "brief"
    ? t("personaOpportunities.hub.scripts.kindBrief", "Brief")
    : t("personaOpportunities.hub.scripts.kindScript", "Script");
}
